import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import type { FileRejection, DropEvent } from 'react-dropzone';
import type { FileUploaderProps, UploadedFile } from './FileUploader.types';
import { FilePreview } from './FilePreview';
import { CloudArrowUpIcon } from './icons';

export const FileUploader: React.FC<FileUploaderProps> = ({
    accept,
    maxSize = 5 * 1024 * 1024, // 5MB default
    multiple = true,
    uploadFunction,
    initialFiles = [],
    variant = 'dropzone', // Sent to style logic if needed later
    autoUpload = true,
    errorMessages,
    label = 'Arrastra y suelta archivos aquí',
    subLabel = 'o haz clic para explorar',
    onFilesChange,
    className = '',
    disabled = false,
    dictionary = {},
    showValidationHints = true,
    icon,
    classNames = {},
}) => {
    const [files, setFiles] = useState<UploadedFile[]>(
        initialFiles.map((f) => ({ ...f, status: 'completed', progress: 100 }))
    );

    // Get hint text from accept and maxSize
    const getHintsText = () => {
        const parts: string[] = [];
        
        if (accept) {
            if (typeof accept === 'string') {
                parts.push(accept.replace(/\./g, '').toUpperCase());
            } else {
                const extensions = Object.values(accept).flat().map(ext => ext.replace('.', '').toUpperCase());
                parts.push(Array.from(new Set(extensions)).join(', '));
            }
        }

        if (maxSize) {
            const mb = maxSize / (1024 * 1024);
            parts.push(`hasta ${mb.toFixed(0)}MB`);
        }

        return parts.join(' ');
    };

    // Notify parent when files change
    useEffect(() => {
        if (onFilesChange) {
            onFilesChange(files);
        }
    }, [files, onFilesChange]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (disabled) return;

            const newFiles: UploadedFile[] = acceptedFiles.map((file) => ({
                id: Math.random().toString(36).substr(2, 9),
                file,
                name: file.name,
                size: file.size,
                type: file.type,
                status: 'pending',
                progress: 0,
                preview: file.type.startsWith('image/')
                    ? URL.createObjectURL(file)
                    : undefined,
            }));

            setFiles((prev) => [...prev, ...newFiles]);

            if (autoUpload) {
                // Trigger upload for each new file immediately
                newFiles.forEach((fileWrapper) => {
                    uploadFile(fileWrapper);
                });
            }
        },
        [disabled, uploadFunction, autoUpload]
    );

    const uploadFile = async (fileWrapper: UploadedFile) => {
        if (!fileWrapper.file) return;

        setFiles((prev) =>
            prev.map((f) =>
                f.id === fileWrapper.id ? { ...f, status: 'uploading' } : f
            )
        );

        try {
            const result = await uploadFunction(fileWrapper.file, (progress) => {
                setFiles((prev) =>
                    prev.map((f) =>
                        f.id === fileWrapper.id ? { ...f, progress } : f
                    )
                );
            });

            const url = typeof result === 'string' ? result : result.url;

            setFiles((prev) =>
                prev.map((f) =>
                    f.id === fileWrapper.id
                        ? { ...f, status: 'completed', progress: 100, url }
                        : f
                )
            );
        } catch (error) {
            console.error('Upload failed:', error);
            setFiles((prev) =>
                prev.map((f) =>
                    f.id === fileWrapper.id
                        ? { ...f, status: 'error', error: dictionary.uploadFailed || 'Error al subir' }
                        : f
                )
            );
        }
    };

    const uploadPendingFiles = () => {
        const pendingFiles = files.filter(f => f.status === 'pending');
        pendingFiles.forEach(f => uploadFile(f));
    };

    const removeFile = (id: string) => {
        const fileToRemove = files.find(f => f.id === id);
        // If we implemented AbortController, we would call abort() here if status is 'uploading'
        setFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
        onDrop,
        accept: accept as any, // Type cast for dropzone compatibility
        maxSize,
        multiple,
        disabled,
    });

    // Validations: fileRejections (from react-dropzone)
    const fileRejectionItems = fileRejections.map(({ file, errors }) => (
        <div key={file.name} className="mt-2 text-sm text-error-content bg-error-bg p-2 rounded">
            <strong>{file.name}</strong> {dictionary.hasErrors || 'tiene errores:'}
            <ul className="list-disc ml-4">
                {errors.map((e) => (
                    <li key={e.code}>
                        {errorMessages?.[e.code] || e.message}
                    </li>
                ))}
            </ul>
        </div>
    ));

    const hasPendingFiles = files.some(f => f.status === 'pending');

    return (
        <div className={`w-full ${className}`}>
            {/* Dropzone Area */}
            <div
                {...getRootProps()}
                className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
          ${isDragActive
                        ? (classNames.dropzoneActive || 'border-primary-500 bg-primary-50 dark:bg-primary-900/20')
                        : (classNames.dropzoneInactive || 'border-border-secondary dark:border-dark-border-secondary hover:border-primary-400 dark:hover:border-primary-400')
                    }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          bg-bg-primary dark:bg-dark-bg-primary
        `}
            >
                <input {...getInputProps()} />

                <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="p-3 bg-bg-secondary dark:bg-dark-bg-tertiary rounded-full">
                        {icon ? (
                            icon
                        ) : (
                            <CloudArrowUpIcon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                        )}
                    </div>
                    <div>
                        <p className="text-lg font-medium text-content-primary dark:text-dark-content-primary">
                            {label}
                        </p>
                        <p className="text-sm text-content-tertiary dark:text-dark-content-tertiary mt-1">
                            {subLabel}
                        </p>
                        {showValidationHints && (
                            <p className="text-[11px] font-normal text-content-tertiary dark:text-dark-content-tertiary mt-2 uppercase tracking-wider">
                                {getHintsText()}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Error Messages for Rejected Files */}
            {fileRejectionItems.length > 0 && (
                <div className="mt-4">
                    {fileRejectionItems}
                </div>
            )}

            {/* File List - Ocultar si autoUpload es true según requerimiento */}
            {!autoUpload && files.length > 0 && (
                <div className="mt-6 space-y-3">
                    {files.map((file) => (
                        <FilePreview
                            key={file.id}
                            file={file}
                            onRemove={removeFile}
                            disabled={disabled}
                            dictionary={dictionary}
                        />
                    ))}
                </div>
            )}

            {/* Manual Upload Action */}
            {!autoUpload && hasPendingFiles && (
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={uploadPendingFiles}
                        className={`px-4 py-2 rounded-md font-medium transition-colors shadow-sm flex items-center ${classNames.submitButton || 'bg-primary-600 hover:bg-primary-700 text-white'}`}
                    >
                        {icon ? icon : <CloudArrowUpIcon className="w-5 h-5 mr-2" />}
                        {dictionary.uploadPendingButton 
                            ? dictionary.uploadPendingButton.replace('{count}', files.filter(f => f.status === 'pending').length.toString()) 
                            : `Subir ${files.filter(f => f.status === 'pending').length} archivos pendientes`
                        }
                    </button>
                </div>
            )}
        </div>
    );
};
